import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../ui/Modal';
import { AccountTypeahead } from '../accounts/AccountTypeahead';
import { useCrmData } from '../../contexts/CrmDataContext';
import { usePortal } from '../../contexts/PortalContext';
import { useModalFormSession } from '../../hooks/useModalFormSession';
import { canAssignProjectTaskOwner, canSelectContactsForLinkedAccount } from '../../lib/accountVisibility';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import {
  activityChangeLine,
  buildActivityChangeDetail,
  logPortalActivityQuiet,
} from '../../lib/portalActivityLog';
import { queueSharePointEnquirySync } from '../../lib/sharepointEnquiry';
import { nextProjectReferenceNo } from '../../lib/projectRef';
import { accountDetailPath } from '../../lib/portal';
import { getDbSupabase } from '../../lib/supabase';
import type { Project } from '../../types/crm';
import { PROJECT_STATUSES } from '../../types/crm';
import {
  clientById,
  clientsForTaskProjectLinking,
  contactLabel,
  contactsForClient,
  idsMatch,
  ownerOptionMatches,
} from '../../utils/crmHelpers';
import { clientMatchesSalespersonCodes } from '../../utils/salespersonScope';
import { ownerIdForUser, ownerLabelForRef } from '../../utils/ownerId';

interface ProjectFormModalProps {
  open: boolean;
  onClose: () => void;
  project?: Project | null;
  defaultClientId?: string;
  lockClient?: boolean;
  defaultTitle?: string;
}

export function ProjectFormModal({
  open,
  onClose,
  project,
  defaultClientId,
  lockClient = false,
  defaultTitle,
}: ProjectFormModalProps) {
  const { kind: portal } = usePortal();
  const {
    data,
    refresh,
    username,
    ownerIdKind,
    ownerOptions,
    effectiveRole,
    departmentSlug,
    actorSalespersonCodes,
  } = useCrmData();

  const canAssignOwner = canAssignProjectTaskOwner(effectiveRole, departmentSlug);

  const linkableClients = useMemo(
    () => clientsForTaskProjectLinking(data.clients),
    [data.clients],
  );

  const assignableOwners = useMemo(
    () => ownerOptions.filter((o) => o.username.toLowerCase() !== 'system'),
    [ownerOptions],
  );

  const isEdit = Boolean(project?.id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [clientId, setClientId] = useState('');
  const [contactId, setContactId] = useState('');
  const [status, setStatus] = useState<string>(PROJECT_STATUSES[0]);
  const [ownerId, setOwnerId] = useState('');

  const account = clientById(data.clients, clientId);

  const accountIsOwnSalesCode = useMemo(
    () => clientMatchesSalespersonCodes(account?.owner_id, actorSalespersonCodes),
    [account?.owner_id, actorSalespersonCodes],
  );

  const canPickContacts = canSelectContactsForLinkedAccount(
    effectiveRole,
    departmentSlug,
    accountIsOwnSalesCode,
  );

  const accountContacts = useMemo(() => {
    if (!clientId || !canPickContacts) return [];
    return contactsForClient(data.contacts, clientId);
  }, [data.contacts, clientId, canPickContacts]);

  const lockedContactLabel = useMemo(() => {
    if (!contactId || canPickContacts) return null;
    const row = data.contacts.find((c) => idsMatch(c.id, contactId));
    return row ? contactLabel(row) : 'Assigned (contact details hidden)';
  }, [contactId, canPickContacts, data.contacts]);

  const sessionKey = `${project?.id ?? 'new'}:${defaultClientId ?? ''}:${defaultTitle ?? ''}`;

  const defaultOwnerValue = useCallback(() => {
    const me = data.users.find((u) => u.username === username);
    if (isEdit && project?.owner_id) {
      const match = assignableOwners.find((o) => ownerOptionMatches(o, project.owner_id!));
      return match?.value ?? project.owner_id;
    }
    if (me) return ownerIdForUser(me, ownerIdKind);
    return username;
  }, [data.users, username, isEdit, project?.owner_id, assignableOwners, ownerIdKind]);

  const initForm = useCallback(() => {
    setTitle(project?.title ?? defaultTitle ?? '');
    const preferred = project?.client_id ?? defaultClientId ?? '';
    const valid =
      preferred && linkableClients.some((c) => idsMatch(c.id, preferred))
        ? preferred
        : '';
    setClientId(valid ? String(valid) : '');
    setContactId(project?.contact_id ? String(project.contact_id) : '');
    setStatus(project?.status ?? PROJECT_STATUSES[0]);
    setOwnerId(defaultOwnerValue());
    setError(null);
  }, [project, defaultClientId, defaultTitle, linkableClients, defaultOwnerValue]);

  useModalFormSession(open, sessionKey, initForm);

  useEffect(() => {
    if (!open || clientId) return;
    const preferred = project?.client_id ?? defaultClientId ?? '';
    const valid =
      preferred && linkableClients.some((c) => idsMatch(c.id, preferred))
        ? String(preferred)
        : '';
    if (valid) setClientId(valid);
  }, [open, clientId, linkableClients, project?.client_id, defaultClientId]);

  useEffect(() => {
    if (!open || !contactId || !canPickContacts) return;
    if (!accountContacts.some((c) => idsMatch(c.id, contactId))) {
      setContactId('');
    }
  }, [open, contactId, accountContacts, canPickContacts]);

  function handleAccountChange(nextClientId: string) {
    setClientId(nextClientId);
    setContactId('');
  }

  function resolveOwnerId(): string {
    if (canAssignOwner && ownerId.trim()) return ownerId.trim();
    return defaultOwnerValue();
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle || !clientId) {
      setError('Project title and account are required.');
      return;
    }

    if (!clientById(linkableClients, clientId)) {
      setError('Select a valid account from the list.');
      return;
    }

    const resolvedOwner = resolveOwnerId();
    if (canAssignOwner && !assignableOwners.some((o) => o.value === resolvedOwner)) {
      setError('Select a valid owner.');
      return;
    }

    setSaving(true);
    setError(null);

    const payload: Record<string, unknown> = {
      title: trimmedTitle,
      status,
      client_id: clientId,
      contact_id: canPickContacts
        ? contactId || null
        : isEdit
          ? (project?.contact_id ?? null)
          : null,
      owner_id: resolvedOwner,
    };

    try {
      if (isEdit && project) {
        const { error: updateError } = await getDbSupabase().from('projects').update(payload).eq('id', project.id);
        if (updateError) throw updateError;
        const detail =
          buildActivityChangeDetail([
            activityChangeLine('Title', project.title ?? '', trimmedTitle),
            activityChangeLine('Status', project.status ?? '', status),
            activityChangeLine(
              'Owner',
              ownerLabelForRef(data.users, project.owner_id),
              ownerLabelForRef(data.users, resolvedOwner),
            ),
          ]) ?? `Project "${trimmedTitle}" updated.`;
        logPortalActivityQuiet({
          portal,
          action: 'update',
          entityType: 'project',
          entityId: project.id,
          clientId,
          title: `Project updated: ${trimmedTitle}`,
          detail: account
            ? `${detail}\nAccount: ${account.company_name} (${account.code})`
            : detail,
        });
      } else {
        payload.reference_no = nextProjectReferenceNo(data.projects);
        const { error: insertError } = await getDbSupabase().from('projects').insert(payload);
        if (insertError) throw insertError;
        logPortalActivityQuiet({
          portal,
          action: 'create',
          entityType: 'project',
          clientId,
          title: `Project created: ${trimmedTitle}`,
          detail: account
            ? `Reference ${String(payload.reference_no)}. Owner: ${ownerLabelForRef(data.users, resolvedOwner)}. Account: ${account.company_name} (${account.code}).`
            : `Reference ${String(payload.reference_no)}. Owner: ${ownerLabelForRef(data.users, resolvedOwner)}.`,
        });
      }
      await refresh();
      queueSharePointEnquirySync();
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err, 'crm'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={isEdit ? 'Edit project' : 'New project'}
      open={open}
      onClose={onClose}
      panelClassName="modal-panel--task-form"
    >
      <form className="crm-form crm-form--modal" onSubmit={(e) => void handleSave(e)}>
        <div className="crm-form__scroll">
        {error && <div className="alert alert-error">{error}</div>}

        <label className="field">
          <span className="field-label">Project title *</span>
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>

        <label className="field">
          <span className="field-label">Account *</span>
          <AccountTypeahead
            clients={linkableClients}
            value={clientId}
            onChange={handleAccountChange}
            disabled={lockClient && Boolean(defaultClientId)}
            placeholder="Search accounts"
          />
          <p className="field-hint">
            {canAssignOwner
              ? 'Any sales user may link any account.'
              : 'Any sales user may link any account. You remain the project owner.'}
          </p>
        </label>

        {canAssignOwner && (
          <label className="field">
            <span className="field-label">Assign to *</span>
            <select
              className="field-input"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              required
            >
              {assignableOwners.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.display_name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="field">
          <span className="field-label">Key contact</span>
          {canPickContacts ? (
            <select
              className="field-input"
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              disabled={!clientId}
            >
              <option value="">— None —</option>
              {accountContacts.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {contactLabel(c)}
                  {c.job_title ? ` · ${c.job_title}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="field-input"
              value={lockedContactLabel ?? '— None —'}
              disabled
              readOnly
            />
          )}
          {clientId && canPickContacts && accountContacts.length === 0 && (
            <p className="field-hint">
              No contacts exist on this account yet.{' '}
              {account && (
                <Link to={accountDetailPath(account.code)} onClick={onClose}>
                  Add a contact
                </Link>
              )}
            </p>
          )}
          {clientId && !canPickContacts ? (
            <p className="field-hint">
              This account is outside your salesperson codes. Contact assignment is limited to Admin,
              IT or Management. Please ask them to set the key contact if required.
            </p>
          ) : null}
        </label>

        <label className="field">
          <span className="field-label">Status</span>
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {PROJECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        {isEdit && project?.reference_no && (
          <p className="field-hint">Reference: {project.reference_no}</p>
        )}
        </div>

        <div className="crm-form__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save project'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
